class LcHomeIntro extends HTMLElement {
  constructor() {
    super();
    this.slides = [];
    this.dots = [];
    this.currentIndex = 0;
    this.timer = null;
    this.touchStartX = 0;
    this.didSwipe = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  }

  connectedCallback() {
    this.hero = this.querySelector('[data-lc-hero]');
    this.slides = [...this.querySelectorAll('[data-lc-slide]')];
    this.dots = [...this.querySelectorAll('[data-lc-dot]')];
    this.needVideos = [...this.querySelectorAll('[data-lc-need-video]')];
    this.previousButton = this.querySelector('[data-lc-previous]');
    this.nextButton = this.querySelector('[data-lc-next]');
    this.interval = Math.max(4, Number(this.dataset.autoplaySeconds) || 5) * 1000;
    this.setupNeedVideos();

    if (!this.hero || this.slides.length < 2) return;

    this.previousButton?.addEventListener('click', this.showPrevious);
    this.nextButton?.addEventListener('click', this.showNext);
    this.dots.forEach((dot) => dot.addEventListener('click', this.handleDotClick));
    this.hero.addEventListener('mouseenter', this.stopAutoplay);
    this.hero.addEventListener('mouseleave', this.startAutoplay);
    this.hero.addEventListener('focusin', this.stopAutoplay);
    this.hero.addEventListener('focusout', this.handleFocusOut);
    this.hero.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.hero.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    this.hero.addEventListener('click', this.handleHeroClick);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.needVideoObserver?.disconnect();
    this.reducedMotion.removeEventListener('change', this.handleNeedMotionChange);
    this.previousButton?.removeEventListener('click', this.showPrevious);
    this.nextButton?.removeEventListener('click', this.showNext);
    this.dots.forEach((dot) => dot.removeEventListener('click', this.handleDotClick));
    this.hero?.removeEventListener('mouseenter', this.stopAutoplay);
    this.hero?.removeEventListener('mouseleave', this.startAutoplay);
    this.hero?.removeEventListener('focusin', this.stopAutoplay);
    this.hero?.removeEventListener('focusout', this.handleFocusOut);
    this.hero?.removeEventListener('touchstart', this.handleTouchStart);
    this.hero?.removeEventListener('touchend', this.handleTouchEnd);
    this.hero?.removeEventListener('click', this.handleHeroClick);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  setupNeedVideos() {
    if (!this.needVideos.length) return;

    this.needVideos.forEach((video) => {
      video.muted = true;
    });
    this.reducedMotion.addEventListener('change', this.handleNeedMotionChange);

    if (!('IntersectionObserver' in window)) {
      if (!this.reducedMotion.matches) {
        this.needVideos.forEach((video) => video.play().catch(() => {}));
      }
      return;
    }

    this.needVideoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          const shouldPlay = entry.isIntersecting && entry.intersectionRatio >= 0.35;
          video.dataset.lcNeedVisible = String(shouldPlay);

          if (shouldPlay && !this.reducedMotion.matches && !document.hidden) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: [0, 0.35, 0.7] }
    );

    this.needVideos.forEach((video) => this.needVideoObserver.observe(video));
  }

  handleNeedMotionChange = () => {
    this.needVideos.forEach((video) => {
      if (this.reducedMotion.matches || video.dataset.lcNeedVisible !== 'true' || document.hidden) {
        video.pause();
      } else {
        video.play().catch(() => {});
      }
    });
  };

  showSlide(index, shouldRestart = true) {
    const nextIndex = (index + this.slides.length) % this.slides.length;
    if (nextIndex === this.currentIndex) return;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === nextIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
      slide.querySelectorAll('a, button').forEach((control) => {
        if (isActive) control.removeAttribute('tabindex');
        else control.setAttribute('tabindex', '-1');
      });
    });

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === nextIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', String(isActive));
    });

    this.currentIndex = nextIndex;
    if (shouldRestart) this.startAutoplay();
  }

  showPrevious = () => this.showSlide(this.currentIndex - 1);

  showNext = () => this.showSlide(this.currentIndex + 1);

  handleDotClick = (event) => {
    this.showSlide(Number(event.currentTarget.dataset.lcDot));
  };

  handleFocusOut = (event) => {
    if (!this.hero.contains(event.relatedTarget)) this.startAutoplay();
  };

  handleTouchStart = (event) => {
    this.touchStartX = event.changedTouches[0]?.clientX || 0;
    this.didSwipe = false;
    this.stopAutoplay();
  };

  handleTouchEnd = (event) => {
    const distance = (event.changedTouches[0]?.clientX || 0) - this.touchStartX;
    if (Math.abs(distance) > 45) {
      this.didSwipe = true;
      this.showSlide(this.currentIndex + (distance < 0 ? 1 : -1));
      window.setTimeout(() => {
        this.didSwipe = false;
      }, 250);
      return;
    }
    this.startAutoplay();
  };

  handleHeroClick = (event) => {
    if (this.didSwipe && event.target.closest('.lc-home-intro__image-link')) {
      event.preventDefault();
    }
  };

  handleVisibilityChange = () => {
    if (document.hidden) {
      this.stopAutoplay();
      this.needVideos.forEach((video) => video.pause());
    } else {
      this.startAutoplay();
      this.handleNeedMotionChange();
    }
  };

  startAutoplay = () => {
    this.stopAutoplay();
    if (this.reducedMotion.matches || document.hidden || this.slides.length < 2) return;
    this.timer = window.setInterval(() => this.showSlide(this.currentIndex + 1, false), this.interval);
  };

  stopAutoplay = () => {
    window.clearInterval(this.timer);
    this.timer = null;
  };
}

if (!customElements.get('lc-home-intro')) {
  customElements.define('lc-home-intro', LcHomeIntro);
}
